import type { Route } from "~/api/types";
import type { SwapSide } from "./SwapCard";
import { useEffect, useState } from "react";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { useUnifiedWallet } from "~/hooks/useUnifiedWallet";
import { useChains } from "~/hooks/useChains";

type ButtonState =
  | "connect"
  | "select-token"
  | "enter-amount"
  | "enter-recipient"
  | "loading"
  | "no-route"
  | "switch-chain"
  | "switching-chain"
  | "approve"
  | "swap"
  | "executing";

function normalizeChainId(chainId: number | string | undefined): string | null {
  if (chainId == null) return null;
  if (typeof chainId === "number") return String(chainId);
  if (chainId.startsWith("0x")) {
    const parsed = Number.parseInt(chainId, 16);
    return Number.isNaN(parsed) ? null : String(parsed);
  }
  return chainId;
}

function getRequiredEvmChainId(route: Route | null): string | null {
  if (!route) return null;

  for (const step of route.steps) {
    switch (step.type) {
      case "APPROVAL":
        return step.tx.type === "evm" ? step.chainId : null;
      case "SWAP":
      case "WRAP":
      case "UNWRAP":
      case "CLAIM":
        return step.tx?.type === "evm" ? step.chainId : null;
      case "BRIDGE":
        return step.tx?.type === "evm" ? step.fromChainId : null;
      default:
        break;
    }
  }

  return null;
}

function getButtonState(
  isConnected: boolean,
  from: SwapSide,
  to: SwapSide,
  selectedRoute: Route | null,
  isStreaming: boolean,
  requiresChainSwitch: boolean,
  isSwitchingChain: boolean,
  isExecuting: boolean,
  needsRecipient?: boolean,
): ButtonState {
  if (!isConnected) return "connect";
  if (!from.token || !to.token) return "select-token";
  if (!from.amount || from.amount === "0") return "enter-amount";
  if (isSwitchingChain) return "switching-chain";
  if (isExecuting) return "executing";
  if (isStreaming && !selectedRoute) return "loading";
  if (!selectedRoute) return "no-route";
  if (needsRecipient) return "enter-recipient";
  if (requiresChainSwitch) return "switch-chain";
  return "swap";
}

const labels: Record<ButtonState, string> = {
  connect: "CONNECT WALLET",
  "select-token": "SELECT TOKEN",
  "enter-amount": "ENTER AMOUNT",
  "enter-recipient": "ENTER RECIPIENT ADDRESS",
  loading: "FINDING ROUTES...",
  "no-route": "NO ROUTE AVAILABLE",
  "switch-chain": "SWITCH CHAIN",
  "switching-chain": "SWITCHING CHAIN...",
  approve: "APPROVE",
  swap: "SWAP",
  executing: "EXECUTING...",
};

export function ActionButton({
  from,
  to,
  selectedRoute,
  isStreaming,
  isExecuting,
  onExecute,
  isCrossChain = false,
  needsRecipient,
}: {
  from: SwapSide;
  to: SwapSide;
  selectedRoute: Route | null;
  isStreaming: boolean;
  isExecuting: boolean;
  onExecute: () => void;
  isCrossChain?: boolean;
  needsRecipient?: boolean;
}) {
  const { isConnected, chainId: walletChainId } = useUnifiedWallet();
  const { primaryWallet, setShowAuthFlow } = useDynamicContext();
  const { getChain } = useChains();
  const [isSwitchingChain, setIsSwitchingChain] = useState(false);
  const [switchErrorMessage, setSwitchErrorMessage] = useState<string | null>(null);
  const requiredChainId = getRequiredEvmChainId(selectedRoute);
  const requiredChain = requiredChainId ? getChain(requiredChainId) : null;
  const currentChain = walletChainId ? getChain(walletChainId) : null;
  const requiresChainSwitch = requiredChainId != null && walletChainId !== requiredChainId;

  const state = getButtonState(
    isConnected,
    from,
    to,
    selectedRoute,
    isStreaming,
    requiresChainSwitch,
    isSwitchingChain,
    isExecuting,
    needsRecipient,
  );
  const disabled =
    state === "select-token" ||
    state === "enter-amount" ||
    state === "enter-recipient" ||
    state === "loading" ||
    state === "no-route" ||
    state === "switching-chain" ||
    state === "executing";

  useEffect(() => {
    setSwitchErrorMessage(null);
  }, [walletChainId, requiredChainId, primaryWallet?.address]);

  const helperText = switchErrorMessage ?? (state === "switch-chain"
    ? `Wallet on ${currentChain?.name ?? "another network"}. This route starts on ${requiredChain?.name ?? `chain ${requiredChainId}`}.`
    : null);

  const handleClick = async () => {
    if (state === "connect") {
      setShowAuthFlow(true);
      return;
    }
    if (state === "switch-chain" && requiredChainId) {
      const nextChainId = Number(requiredChainId);
      if (Number.isNaN(nextChainId)) return;
      setSwitchErrorMessage(null);
      setIsSwitchingChain(true);
      try {
        if (!primaryWallet) {
          throw new Error("No connected wallet available to switch networks.");
        }

        if (!primaryWallet.connector.supportsNetworkSwitching()) {
          throw new Error(`Switch networks in your wallet to ${requiredChain?.name ?? `chain ${requiredChainId}`}.`);
        }

        await primaryWallet.switchNetwork(nextChainId);
        const syncDeadline = Date.now() + 3000;
        while (Date.now() < syncDeadline) {
          const activeNetwork = normalizeChainId(await primaryWallet.getNetwork());
          if (activeNetwork === requiredChainId) {
            break;
          }
          await new Promise((resolve) => window.setTimeout(resolve, 150));
        }
      } catch (error) {
        setSwitchErrorMessage(error instanceof Error ? error.message : "Failed to switch network.");
      } finally {
        setIsSwitchingChain(false);
      }
      return;
    }
    if (state === "swap" || state === "approve") {
      onExecute();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <button
        onClick={() => {
          void handleClick();
        }}
        disabled={disabled}
        className={`w-full rounded-xl py-4 text-sm font-bold tracking-wide uppercase transition-colors ${
          disabled
            ? "cursor-not-allowed bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] opacity-60"
            : "bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]"
        }`}
      >
        {state === "loading" || state === "executing" || state === "switching-chain" ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="h-4 w-4 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
                className="opacity-25"
              />
              <path
                d="M4 12a8 8 0 018-8"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
            {state === "switching-chain" && requiredChain
              ? `SWITCHING TO ${requiredChain.name.toUpperCase()}...`
              : labels[state]}
          </span>
        ) : state === "switch-chain" && requiredChain ? (
          `SWITCH TO ${requiredChain.name.toUpperCase()}`
        ) : (
          state === "swap" && isCrossChain && selectedRoute
            ? `BRIDGE VIA ${selectedRoute.summary.provider.name.toUpperCase()}`
            : labels[state]
        )}
      </button>
      {helperText ? (
        <p className="text-xs text-[var(--color-text-secondary)]">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
